import classNames from 'classnames';
import { IntroEvent } from 'common/interfaces/api';
import { SAXO_10000_COIN_EVENT, useMediaQuery } from 'common/utils';
import { getSaxoEvent } from 'common/utils/user';
import dayjs from 'lib/dayjs';
import { useMemo } from 'react';
import { useQuery } from 'react-query';
import { getEvents, MissionCode, MissionTaskStatus } from 'service/user';
import useGlobalStore from 'store/global';
import useStore from 'store/timeline';

import styles from './SaxoCampaignBanner.module.scss';

const SAXO_DEFAULT_COIN = 5000;

interface SaxoCampaignBannerPropsType {
  className?: string;
  onAfterClick?: () => void;
  initialCoinNumber?: number;
  saxoEvent?: IntroEvent;
}

const SaxoCampaignBanner = ({
  className,
  onAfterClick,
  initialCoinNumber,
  saxoEvent: saxoEventProp,
}: SaxoCampaignBannerPropsType) => {
  const isMobile = useMediaQuery('(max-width: 767px)');
  const currentUser = useStore((state) => state.currentUser);
  const allMissions = useStore((state) => state.allMissions);
  const currentAuth = useGlobalStore((state) => state.currentAuth);
  const showLogin = useGlobalStore((state) => state.showLogin);
  const showMissionDialog = useGlobalStore((state) => state.showMissionDialog);

  const saxoMission = useMemo(() => {
    return allMissions.find(
      (mission) => mission.code === MissionCode.SaxoCreateAccount,
    );
  }, [allMissions]);

  const { data: eventsData } = useQuery(
    ['getSaxoPromotionEvents'],
    async () => getEvents(),
    { enabled: !saxoMission && !saxoEventProp },
  );

  const eventFromApi = useMemo(() => {
    return eventsData?.data?.find(
      (event) => event.type === SAXO_10000_COIN_EVENT,
    );
  }, [eventsData]);

  const saxoEvent = getSaxoEvent(currentUser) || saxoEventProp || eventFromApi;

  const isMissionInProgress =
    !!saxoMission &&
    [MissionTaskStatus.NotYet, MissionTaskStatus.InProgress].includes(
      saxoMission.current_task?.status,
    );

  const buttonLabel = !currentAuth
    ? 'ログインして\n今すぐ口座開設'
    : isMissionInProgress
      ? 'クリックして\n詳細を確認する'
      : '申込済み\n一覧をみる';

  const isMissionDone =
    !!saxoMission &&
    saxoMission.current_task?.status === MissionTaskStatus.Done;

  const endDateLabel =
    saxoEvent?.end_at && !isMissionDone
      ? `${dayjs(saxoEvent.end_at).format('M/D')}まで期間限定`
      : null;

  const eventCoinNumber = eventFromApi ? 10000 : undefined;
  const showingCoinNumber =
    saxoMission?.coin ||
    initialCoinNumber ||
    eventCoinNumber ||
    SAXO_DEFAULT_COIN;
  const imageUrl = isMobile
    ? showingCoinNumber >= 10000
      ? '/images/banners/saxo-popup-mobile-pr.png'
      : '/images/banners/saxo-popup-mobile-5000-pr.png'
    : showingCoinNumber >= 10000
      ? '/images/banners/saxo-popup-pr.png'
      : '/images/banners/saxo-popup-5000-pr.png';

  const handleButtonClick = () => {
    if (!currentAuth) {
      showLogin({});
      return;
    }

    if (!saxoMission) return;

    if (
      [MissionTaskStatus.NotYet, MissionTaskStatus.InProgress].includes(
        saxoMission?.current_task?.status,
      )
    ) {
      const fromLp =
        typeof window !== 'undefined' &&
        window.location.pathname.includes('/p/saxo_mission');
      window.open(saxoMission.link, '_blank')?.focus();
      onAfterClick?.();
    } else {
      showMissionDialog(true);
    }
  };

  return (
    <div
      className={classNames(
        isMobile ? styles.wrapperMobile : styles.wrapper,
        className,
      )}
    >
      <img src={imageUrl} alt="saxo campaign" className={styles.bannerImage} />
      {endDateLabel && (
        <div className={styles.endDateBadge}>{endDateLabel}</div>
      )}
      <button
        type="button"
        className={styles.overlayBtn}
        onClick={handleButtonClick}
      >
        <span>{buttonLabel}</span>
      </button>
      {isMobile && (
        <div className={styles.disclaimer}>
          ※コインに関するお問い合わせは、
          <br />
          PostPrimeまでご連絡ください。
        </div>
      )}
      <div className={styles.helpLink}>
        コインキャンペーンの詳細は
        <a
          href="https://help.postprime.com/hc/ja/articles/5569669037071#h_01JRCJ15ESNT30C9AJCJF6JHQC"
          target="_blank"
          rel="noreferrer"
        >
          こちら
        </a>
        をご覧ください。
      </div>
    </div>
  );
};

export default SaxoCampaignBanner;
